import { render } from "solid-js/web";
import { createComponent } from "solid-js";
import { Menu, replayLabel, setListVersion, setLiveError, setLoadError, setResumable, setSection, type Section } from "../menu/Menu";
import { ByteSource, ReplayFile } from "../replay/replay";
import { $, bytes } from "./format";
import { hideLoading, setStage, showLoading } from "./loading";

export type Loaded = (replay: ReplayFile, name: string) => void;

/** The main menu screen (a Solid component, `menu/Menu.tsx`) and replay
 *  loading. The app drives it through this class. */
export class Loader {
  private root = $("loader");
  private busy = false;
  /** Back to the battle (set while one is loaded). */
  onResume: (() => void) | null = null;
  /** Opens the atlas screen (set by the app). */
  onAtlas: (() => void) | null = null;
  /** Starts a battle from Play (`query` as in `?live&...`; set by the app). */
  onPlay: ((query: string) => void) | null = null;

  constructor(private loaded: Loaded) {
    render(
      () =>
        createComponent(Menu, {
          actions: {
            play: (q) => this.onPlay?.(q),
            replay: (path) => void this.loadUrl(path),
            file: (f) => void this.loadFile(f),
            atlas: () => this.onAtlas?.(),
            resume: () => this.onResume?.(),
          },
        }),
      this.root,
    );
    window.addEventListener("dragover", (e) => {
      e.preventDefault();
      this.root.classList.add("dropping");
      this.show(!!this.onResume, "replays");
    });
    window.addEventListener("dragleave", (e) => {
      if (e.relatedTarget === null) this.root.classList.remove("dropping");
    });
    window.addEventListener("drop", (e) => {
      e.preventDefault();
      this.root.classList.remove("dropping");
      const f = e.dataTransfer?.files[0];
      if (f) void this.loadFile(f);
    });
  }

  /** Shows the menu; `resumable`: a battle is loaded to go back to. */
  show(resumable = false, section?: string): void {
    this.root.classList.remove("hidden");
    setResumable(resumable && !!this.onResume);
    if (section) this.section(section);
    setListVersion((v) => v + 1);
  }

  get open(): boolean {
    return !this.root.classList.contains("hidden");
  }

  /** One of the menu's sections: play, replays, settings (atlas: its screen). */
  section(name: string): void {
    if (name === "atlas") this.onAtlas?.();
    else setSection(name as Section);
  }

  hide(): void {
    this.root.classList.add("hidden");
  }

  /** A live connection failed or closed (shown on Play). */
  liveError(text: string): void {
    setLiveError(text);
  }

  /** Loads a replay from the server's replay folder by file name. */
  async loadUrl(name: string): Promise<void> {
    await this.run(name, async () => {
      const source = await ByteSource.fetch(`replays/${name.split("/").map(encodeURIComponent).join("/")}`);
      source.onProgress(() => {
        if (this.busy) setStage(`Downloading: ${source.total ? `${bytes(source.received)} of ${bytes(source.total)}` : bytes(source.received)}`, source.progress);
      });
      return source;
    });
    const url = new URL(location.href);
    if (url.searchParams.get("replay") !== name) {
      url.search = "";
      url.searchParams.set("replay", name);
      history.replaceState(null, "", url);
    }
  }

  async loadFile(file: File): Promise<void> {
    await this.run(file.name, async () => ByteSource.whole(new Uint8Array(await file.arrayBuffer())));
    const url = new URL(location.href);
    url.search = "";
    history.replaceState(null, "", url);
  }

  /** Opens the replay as soon as its header has arrived; the rest streams in. */
  private async run(name: string, get: () => Promise<ByteSource>): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    setLoadError("");
    showLoading(replayLabel(name), "Connecting");
    try {
      const source = await get();
      const replay = await ReplayFile.open(source);
      // Start once the first chunk is here, not the whole file.
      setStage("Loading the first frames");
      if (replay.header.chunks.length) await replay.chunk(0);
      this.hide();
      this.loaded(replay, name);
      hideLoading();
    } catch (e) {
      hideLoading();
      this.show(false, "replays");
      setLoadError(e instanceof Error ? e.message : String(e));
    } finally {
      this.busy = false;
    }
  }
}
